<script setup lang="ts">
import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { ERdrChannel, RdrChannelKey } from '@/types/enum/RdrChannelEnum'
import type { CheckpointAssociateErrorModel } from '@/types/models/CheckpointModel'
import type { AssociatedDeviceModel } from '@/types/models/DeviceModel'
import { ECheckpointsPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import {
    CreateButton,
    FullScreenLoader,
    InformationBaseCard,
    PermissionWrapper,
    SecondaryButton,
    StackLayout
} from '@rfc/rfc-components-library'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import DeviceAvailableRdrChannels from '../../../components/DeviceComponents/components/DeviceAvailableRdrChannels.vue'
import CheckpointAssociatedDeviceCard from './components/CheckpointAssociatedDeviceCard.vue'
import CheckpointDeviceInput from './components/CheckpointDeviceInput.vue'

const store = useStore()
const { t } = useI18n()
const route = useRoute()
const eventId = computed(() => parseInt(route.params.event_id.toString()))

type Props = {
    associates: AssociatedDeviceModel[]
    checkpointId?: number
}
const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'refresh'): void }>()

const device = ref<AssociatedDeviceModel>()

const submitted = ref(false)
const editMode = ref(false)
const loading = ref(false)

const selectedRdrChannel = ref<ERdrChannel | undefined>()

const associateError = ref<CheckpointAssociateErrorModel[]>()
const associateDevice = (force?: boolean) => {
    if (editMode.value) {
        submitted.value = true
        if (!device.value || !props.checkpointId) return // TODO : Alerte
        loading.value = true
        return store
            ?.dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.ASSOCIATE_DEVICE}`, {
                device_id: device.value?.device_id ?? device.value?.id,
                checkpoint_id: props.checkpointId,
                event_id: eventId.value,
                config: selectedRdrChannel.value
                    ? { [RdrChannelKey]: selectedRdrChannel.value }
                    : undefined,
                force
            })
            .then(() => {
                emit('refresh')
                editMode.value = false
                submitted.value = false
                device.value = undefined
            })
            .catch(err => (associateError.value = err))
            .finally(() => {
                if (force) associateError.value = undefined
                loading.value = false
            })
    }
    editMode.value = true
}
const label = computed(() =>
    t(editMode.value ? 'checkpoint.detail.associateDevice' : 'checkpoint.detail.associates')
)
</script>

<template>
    <FullScreenLoader :loading="loading" />
    <InformationBaseCard :label="$t('checkpoint.title.associateDeviceTitle')">
        <StackLayout :gap="16" class="w-full space-between" direction="column">
            <StackLayout direction="column" :gap="8">
                <StackLayout v-if="associates.length" direction="column" :gap="8">
                    <StackLayout :gap="8" wrap>
                        <CheckpointAssociatedDeviceCard
                            v-for="(device, key) in associates"
                            :key="key"
                            :checkpointId="checkpointId"
                            @refresh="$emit('refresh')"
                            :device="device" />
                    </StackLayout>
                </StackLayout>
                <span v-else>{{ $t('checkpoint.detail.noDeviceAssociated') }}</span>
            </StackLayout>
            <PermissionWrapper :permission="ECheckpointsPerm.CHECKPOINTS_EDIT">
                <StackLayout direction="column" :gap="16" class="pt-4 associate-device-section">
                    <DeviceAvailableRdrChannels
                        :device="device"
                        :associates="associates"
                        :associateError="associateError"
                        :selectedRdrChannel="selectedRdrChannel"
                        @selectChannel="selectedRdrChannel = $event" />
                    <CheckpointDeviceInput
                        v-if="editMode"
                        :associates="associates"
                        @select="device = $event"
                        class="align-self-end w-full" />
                    <StackLayout
                        direction="row"
                        :gap="8"
                        class="align-self-end"
                        isResponsiveSmallScreen>
                        <SecondaryButton
                            v-if="editMode"
                            :label="$t('common.cancel')"
                            @click="editMode = false" />
                        <CreateButton
                            v-if="associateError"
                            :label="$t('checkpoint.buttons.forceAssociate')"
                            @click="associateDevice(true)" />
                        <CreateButton
                            :loading="loading"
                            :disabled="!!associateError"
                            :label="label"
                            @click="associateDevice" />
                    </StackLayout>
                </StackLayout>
            </PermissionWrapper>
        </StackLayout>
    </InformationBaseCard>
</template>

<style scoped lang="scss"></style>
